import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

import { Loader2, Ban } from "lucide-react";

export default function AdminSubscriptions() {
  
  const queryClient = useQueryClient();
  const [cancelDialog, setCancelDialog] = useState<{ open: boolean; subscription: any }>({ open: false, subscription: null });
  const [cancelReason, setCancelReason] = useState("");
  const [immediateTermination, setImmediateTermination] = useState(false);
  const [cancelPayPal, setCancelPayPal] = useState(true);

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["/api/admin/subscriptions"],
    queryFn: async () => {
      return await apiRequest("GET", "/api/admin/subscriptions");
    }
  });

  const cancelMutation = useApiMutation({
    endpoint: (vars: { subscriptionId: string; reason?: string; immediateTermination?: boolean; cancelPayPal?: boolean }) => `/api/admin/subscriptions/${vars.subscriptionId}/cancel`,
    method: "POST",
    invalidate: [["/api/admin/subscriptions"]],
    successMessage: "Subscription cancelled successfully",
    errorMessage: (error: any) => error.message || "Failed to cancel subscription",
    onSuccess: () => {
      setCancelDialog({ open: false, subscription: null });
      setCancelReason("");
      setImmediateTermination(false);
      setCancelPayPal(true);
    },
  });

  const handleCancel = () => {
    if (cancelDialog.subscription) {
      cancelMutation.mutate({
        subscriptionId: cancelDialog.subscription.subscriptionId,
        reason: cancelReason,
        immediateTermination,
        cancelPayPal
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold">PayPal Subscriptions</h1>
        <p className="text-muted-foreground">Manage all PayPal subscriptions</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Subscriptions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Subscription ID</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.isArray(subscriptions) && subscriptions.map((sub: any) => (
                <TableRow key={sub._id}>
                  <TableCell>
                    <div className="font-medium">
                      {sub.user?.firstName || sub.user?.lastName
                        ? `${sub.user.firstName} ${sub.user.lastName}`.trim()
                        : sub.user?.email || 'Unknown'}
                    </div>
                    <div className="text-xs text-muted-foreground">{sub.user?.email}</div>
                  </TableCell>
                  <TableCell>{sub.plan?.name || 'N/A'}</TableCell>
                  <TableCell className="capitalize">{sub.billingCycle}</TableCell>
                  <TableCell>₱{sub.amount}</TableCell>
                  <TableCell>
                    <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                      {sub.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{sub.subscriptionId}</TableCell>
                  <TableCell>{new Date(sub.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {sub.status === 'active' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setCancelDialog({ open: true, subscription: sub })}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={cancelDialog.open} onOpenChange={(open) => {
        setCancelDialog({ open, subscription: null });
        if (!open) {
          setCancelReason("");
          setImmediateTermination(false);
          setCancelPayPal(true);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to cancel this subscription for{" "}
                <strong>{cancelDialog.subscription?.user.email}</strong>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Subscription ID: <span className="font-mono">{cancelDialog.subscription?.subscriptionId}</span>
              </p>
            </div>
            
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="cancelPayPal" 
                  checked={cancelPayPal}
                  onCheckedChange={(checked) => setCancelPayPal(checked as boolean)}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="cancelPayPal"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Cancel PayPal subscription
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Cancel the subscription on PayPal (recommended). If unchecked, only local access will be revoked.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="immediateTermination" 
                  checked={immediateTermination}
                  onCheckedChange={(checked) => setImmediateTermination(checked as boolean)}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="immediateTermination"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    End access immediately
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Revoke access now instead of at the end of the billing period. Use for refunds or violations.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Cancellation Reason (Required)</Label>
              <Textarea
                id="reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation (e.g., refund requested, policy violation)..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog({ open: false, subscription: null })}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={cancelMutation.isPending || !cancelReason.trim()}
            >
              {cancelMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
